// ── Accesszen Contracts — Zod schemas (source of truth) ──────────────────────
// These schemas mirror shared/accesszen/accesszen/core/decision.py — keep in sync.
// Types are derived via z.infer<> — never hand-written alongside schemas.

import { z } from 'zod'

import { ORG_ROLES } from './roles'

// ── Org member management schemas ─────────────────────────────────────────────

/** Org member as returned by GET /v1/org/members */
export const OrgMemberSchema = z.object({
  user_id: z.string(),
  email: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  /** null = the member holds no assignable g2 org role (shown as "No role"). Ref: ASC-10564 */
  org_role: z.enum(ORG_ROLES).nullable(),
  is_active: z.boolean(),
  joined_at: z.string().nullable(),
})

/**
 * Colleague directory entry from `GET /api/my/org-members` — the member-safe
 * share-picker feed.
 *
 * Deliberately NOT `OrgMemberSchema`: no `org_role`, `is_active` or `joined_at`.
 * Those belong to the org-wide user directory, which stays behind
 * platform:users (org_admin only). A member who may share a saved metric has
 * not thereby earned the right to read everyone's role. Ref: ASC-9563.
 */
export const OrgMemberDirectoryEntrySchema = z.object({
  user_id: z.string(),
  email: z.string(),
  first_name: z.string(),
  last_name: z.string(),
})

/** User search result from local DB (for Add User dialog) */
export const UserSearchResultSchema = z.object({
  user_id: z.string(),
  email: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  /** Current org role if the user already has a membership (e.g. org:viewer); null if not a member */
  current_org_role: z.string().nullable(),
  /** True when the user exists in the IdP but has never logged into Scorpio (no local DB row).
   *  These users cannot be added via Path A — route them to Path B (invite) instead. */
  is_idp_only: z.boolean().default(false),
})

/** Pending org invitation (Path B — email invite flow) */
export const UserInvitationSchema = z.object({
  id: z.string(),
  email: z.string(),
  org_role: z.enum(ORG_ROLES),
  status: z.enum(['pending', 'accepted', 'expired']),
  expires_at: z.string(),
  created_at: z.string(),
})

/** Binary workspace access entry (granted workspaces for a user) */
export const WorkspaceAccessEntrySchema = z.object({
  workspace_id: z.string(),
  workspace_name: z.string(),
  granted_at: z.string().nullable(),
})

/** Agent role entry from Casbin g2 — user_id + role within an agent */
export const AgentRoleEntrySchema = z.object({
  user_id: z.string(),
  role: z.string(),
})

/** Resolved per-user per-agent role (agent_name + role, null = no access) */
export const UserAgentRoleSchema = z.object({
  agent_name: z.string(),
  role: z.string().nullable(),
})

/**
 * GET /v1/agent-roles/for-user — one user's tier per registered agent
 * (`{agent_name: tier | null}`), resolved like PermissionSnapshot.agent_roles.
 * Ref: ASC-10648
 */
export const AgentRolesForUserResponseSchema = z.record(z.string(), z.string().nullable())

/** Platform Developer capability-flag status for one user (G1, per-org). */
export const PlatformDeveloperStatusSchema = z.object({
  user_id: z.string(),
  is_platform_developer: z.boolean(),
})

/** Generic paginated response wrapper */
export function PaginatedResponseSchema<T extends z.ZodTypeAny>(itemSchema: T) {
  return z.object({
    items: z.array(itemSchema),
    total: z.number(),
    page: z.number(),
    page_size: z.number(),
  })
}

// ── Permission decision schemas ───────────────────────────────────────────────

/** Logical permission check — NOT URL paths or HTTP methods. */
export const PermissionCheckSchema = z.object({
  subject: z.string(),
  domain: z.string(),
  resource: z.string(),
  action: z.string(),
})

/** Result of a permission check. */
export const PermissionDecisionSchema = z.object({
  allowed: z.boolean(),
  reason: z.string().optional(),
  checked_at: z.number(),
})

/** A single resolved permission entry. */
export const ResolvedPermissionSchema = z.object({
  resource: z.string(),
  action: z.string(),
  allowed: z.boolean(),
})

/** One workspace the principal can access, paired with its bound agent_ids —
 *  powers the "My Access" workspace x agent matrix. Distinct from
 *  WorkspaceAccessEntrySchema (per-user admin grant view, has granted_at). */
export const WorkspaceAgentAccessEntrySchema = z.object({
  workspace_id: z.string(),
  workspace_name: z.string(),
  agent_ids: z.array(z.string()),
})

/** Point-in-time snapshot of all permissions for a principal (multi-domain). */
export const PermissionSnapshotSchema = z.object({
  principal_id: z.string(),
  org_id: z.string(),
  // Highest directly-assigned org role, display only. Optional/nullable so a gateway
  // deployed before ASC-11219 still parses. Ref: ASC-11219
  org_role: z.string().nullish(),
  org_permissions: z.array(ResolvedPermissionSchema),
  workspace_access: z.array(z.string()),
  workspace_access_detail: z.array(WorkspaceAgentAccessEntrySchema).default([]),
  agent_roles: z.record(z.string(), z.string()),
  agent_permissions: z.record(z.string(), z.array(ResolvedPermissionSchema)),
  generated_at: z.number(),
})
